import * as THREE from 'three';

export interface ParticleSpawn {
  x: number;
  y: number;
  z: number;
  vx?: number;
  vy?: number;
  vz?: number;
  color: THREE.Color;
  /** Colour at end of life (defaults to `color`). */
  colorEnd?: THREE.Color;
  size: number;
  sizeEnd?: number;
  life: number;
  gravity?: number;
  drag?: number;
  alpha?: number;
  /** Stretch along velocity (sparks). */
  stretch?: number;
}

const VERT = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iColor;
attribute vec2 iSize; // x = size, y = stretch
attribute vec3 iVel;
varying vec4 vColor;
varying vec2 vUv;
void main() {
  vUv = uv;
  vColor = iColor;
  vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
  vec2 corner = position.xy * iSize.x;
  if (iSize.y > 0.0) {
    // Stretch the billboard along the projected velocity for sparks.
    vec3 vv = (modelViewMatrix * vec4(iVel, 0.0)).xyz;
    vec2 dir = length(vv.xy) > 1e-4 ? normalize(vv.xy) : vec2(0.0, 1.0);
    vec2 side = vec2(-dir.y, dir.x);
    corner = side * position.x * iSize.x + dir * position.y * iSize.x * (1.0 + iSize.y * length(iVel) * 0.15);
  }
  mv.xy += corner;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform sampler2D map;
varying vec4 vColor;
varying vec2 vUv;
void main() {
  vec4 t = texture2D(map, vUv);
  gl_FragColor = vec4(vColor.rgb * t.rgb, vColor.a * t.a);
  if (gl_FragColor.a < 0.003) discard;
}`;

/**
 * CPU-simulated, GPU-drawn billboard particles in a single instanced draw
 * call. Colours are HDR (values > 1 bloom).
 */
export class ParticleSystem {
  readonly mesh: THREE.Mesh;
  private count = 0;
  private readonly max: number;
  private pos: Float32Array;
  private vel: Float32Array;
  private col: Float32Array;
  private colStart: Float32Array;
  private colEnd: Float32Array;
  private size: Float32Array;
  private sizeRange: Float32Array;
  private life: Float32Array;
  private age: Float32Array;
  private phys: Float32Array; // gravity, drag, alpha, stretch
  private aPos: THREE.InstancedBufferAttribute;
  private aCol: THREE.InstancedBufferAttribute;
  private aSize: THREE.InstancedBufferAttribute;
  private aVel: THREE.InstancedBufferAttribute;
  private geo: THREE.InstancedBufferGeometry;

  constructor(max: number, texture: THREE.Texture, blending: THREE.Blending) {
    this.max = max;
    this.pos = new Float32Array(max * 3);
    this.vel = new Float32Array(max * 3);
    this.col = new Float32Array(max * 4);
    this.colStart = new Float32Array(max * 3);
    this.colEnd = new Float32Array(max * 3);
    this.size = new Float32Array(max * 2);
    this.sizeRange = new Float32Array(max * 2);
    this.life = new Float32Array(max);
    this.age = new Float32Array(max);
    this.phys = new Float32Array(max * 4);

    const base = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.getAttribute('position'));
    this.geo.setAttribute('uv', base.getAttribute('uv'));
    this.aPos = new THREE.InstancedBufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.InstancedBufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage);
    this.aSize = new THREE.InstancedBufferAttribute(this.size, 2).setUsage(THREE.DynamicDrawUsage);
    this.aVel = new THREE.InstancedBufferAttribute(this.vel, 3).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('iPos', this.aPos);
    this.geo.setAttribute('iColor', this.aCol);
    this.geo.setAttribute('iSize', this.aSize);
    this.geo.setAttribute('iVel', this.aVel);
    this.geo.instanceCount = 0;

    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { map: { value: texture } },
      transparent: true,
      depthWrite: false,
      blending,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
  }

  get alive(): number {
    return this.count;
  }

  spawn(p: ParticleSpawn): void {
    if (this.count >= this.max) return;
    const i = this.count++;
    this.pos[i * 3] = p.x;
    this.pos[i * 3 + 1] = p.y;
    this.pos[i * 3 + 2] = p.z;
    this.vel[i * 3] = p.vx ?? 0;
    this.vel[i * 3 + 1] = p.vy ?? 0;
    this.vel[i * 3 + 2] = p.vz ?? 0;
    this.colStart[i * 3] = p.color.r;
    this.colStart[i * 3 + 1] = p.color.g;
    this.colStart[i * 3 + 2] = p.color.b;
    const e = p.colorEnd ?? p.color;
    this.colEnd[i * 3] = e.r;
    this.colEnd[i * 3 + 1] = e.g;
    this.colEnd[i * 3 + 2] = e.b;
    this.sizeRange[i * 2] = p.size;
    this.sizeRange[i * 2 + 1] = p.sizeEnd ?? p.size;
    this.life[i] = p.life;
    this.age[i] = 0;
    this.phys[i * 4] = p.gravity ?? 0;
    this.phys[i * 4 + 1] = p.drag ?? 0;
    this.phys[i * 4 + 2] = p.alpha ?? 1;
    this.phys[i * 4 + 3] = p.stretch ?? 0;
  }

  update(dt: number): void {
    let i = 0;
    while (i < this.count) {
      this.age[i] = (this.age[i] as number) + dt;
      const life = this.life[i] as number;
      const age = this.age[i] as number;
      if (age >= life) {
        this.kill(i);
        continue;
      }
      const t = age / life;
      const g = this.phys[i * 4] as number;
      const drag = this.phys[i * 4 + 1] as number;
      const damp = Math.exp(-drag * dt);
      this.vel[i * 3] = (this.vel[i * 3] as number) * damp;
      this.vel[i * 3 + 1] = ((this.vel[i * 3 + 1] as number) - g * dt) * damp;
      this.vel[i * 3 + 2] = (this.vel[i * 3 + 2] as number) * damp;
      this.pos[i * 3] = (this.pos[i * 3] as number) + (this.vel[i * 3] as number) * dt;
      this.pos[i * 3 + 1] = (this.pos[i * 3 + 1] as number) + (this.vel[i * 3 + 1] as number) * dt;
      this.pos[i * 3 + 2] = (this.pos[i * 3 + 2] as number) + (this.vel[i * 3 + 2] as number) * dt;
      const a = (this.phys[i * 4 + 2] as number) * (t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85);
      this.col[i * 4] = lerp(this.colStart[i * 3] as number, this.colEnd[i * 3] as number, t);
      this.col[i * 4 + 1] = lerp(this.colStart[i * 3 + 1] as number, this.colEnd[i * 3 + 1] as number, t);
      this.col[i * 4 + 2] = lerp(this.colStart[i * 3 + 2] as number, this.colEnd[i * 3 + 2] as number, t);
      this.col[i * 4 + 3] = Math.max(0, a);
      this.size[i * 2] = lerp(this.sizeRange[i * 2] as number, this.sizeRange[i * 2 + 1] as number, t);
      this.size[i * 2 + 1] = this.phys[i * 4 + 3] as number;
      i++;
    }
    this.geo.instanceCount = this.count;
    this.aPos.needsUpdate = true;
    this.aCol.needsUpdate = true;
    this.aSize.needsUpdate = true;
    this.aVel.needsUpdate = true;
  }

  private kill(i: number): void {
    const last = --this.count;
    if (i === last) return;
    this.copy(last, i);
  }

  private copy(from: number, to: number): void {
    const c3 = (arr: Float32Array) => {
      arr[to * 3] = arr[from * 3] as number;
      arr[to * 3 + 1] = arr[from * 3 + 1] as number;
      arr[to * 3 + 2] = arr[from * 3 + 2] as number;
    };
    c3(this.pos);
    c3(this.vel);
    c3(this.colStart);
    c3(this.colEnd);
    for (let k = 0; k < 4; k++) {
      this.col[to * 4 + k] = this.col[from * 4 + k] as number;
      this.phys[to * 4 + k] = this.phys[from * 4 + k] as number;
    }
    this.size[to * 2] = this.size[from * 2] as number;
    this.size[to * 2 + 1] = this.size[from * 2 + 1] as number;
    this.sizeRange[to * 2] = this.sizeRange[from * 2] as number;
    this.sizeRange[to * 2 + 1] = this.sizeRange[from * 2 + 1] as number;
    this.life[to] = this.life[from] as number;
    this.age[to] = this.age[from] as number;
  }

  clear(): void {
    this.count = 0;
    this.geo.instanceCount = 0;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Procedural particle textures. */
export function particleTextures(): { glow: THREE.Texture; smoke: THREE.Texture; spark: THREE.Texture } {
  const mk = (draw: (g: CanvasRenderingContext2D, s: number) => void): THREE.Texture => {
    const s = 64;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const g = c.getContext('2d');
    if (!g) throw new Error('2D canvas unavailable');
    draw(g, s);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };
  const glow = mk((g, s) => {
    const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.25, 'rgba(255,255,255,0.8)');
    grd.addColorStop(0.6, 'rgba(255,255,255,0.18)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });
  const smoke = mk((g, s) => {
    for (let i = 0; i < 18; i++) {
      const x = s / 2 + (Math.random() - 0.5) * s * 0.35;
      const y = s / 2 + (Math.random() - 0.5) * s * 0.35;
      const r = s * (0.15 + Math.random() * 0.2);
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, 'rgba(255,255,255,0.22)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, s, s);
    }
  });
  const spark = mk((g, s) => {
    const grd = g.createLinearGradient(0, 0, 0, s);
    grd.addColorStop(0, 'rgba(255,255,255,0)');
    grd.addColorStop(0.5, 'rgba(255,255,255,1)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(s * 0.4, 0, s * 0.2, s);
  });
  return { glow, smoke, spark };
}
